import React, { useState, useRef, useEffect } from 'react';
import logo from '../logo.png';
import ProposalPage from './ProposalPage';

function ClientCard({ entry, idx, onCreateProposal, onDelete }) {
  const canvasRef = useRef(null);
  const [customization, setCustomization] = useState('');
  const [sendStatus, setSendStatus] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm('Delete this client project?')) return;
    setDeleting(true);
    try {
      if (entry.id) {
        await fetch(`/clients/${entry.id}`, { method: 'DELETE' });
      }
    } catch {}
    onDelete(entry.id);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const stallImg = new Image();
    stallImg.crossOrigin = 'anonymous';
    stallImg.src = entry.stall.images?.[0] || '';
    stallImg.onload = () => {
      ctx.drawImage(stallImg, 0, 0, canvas.width, canvas.height);
      if (!entry.logos?.length) return;
      entry.logos.forEach((logoItem) => {
        const img = new Image();
        img.src = logoItem.src;
        img.onload = () => {
          const x = (logoItem.pos.x / 100) * canvas.width;
          const y = (logoItem.pos.y / 100) * canvas.height;
          ctx.drawImage(img, x, y, logoItem.size, logoItem.size);
        };
      });
    };
    stallImg.onerror = () => {
      ctx.fillStyle = entry.stall.color;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = '48px serif';
      ctx.textAlign = 'center';
      ctx.fillText(entry.stall.icon, canvas.width / 2, canvas.height / 2 + 16);
    };
  }, [entry]);

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : null;
  const eventDateFormatted = entry.eventDateFrom
    ? `${fmt(entry.eventDateFrom)}${entry.eventDateTo ? ' — ' + fmt(entry.eventDateTo) : ''}`
    : (entry.eventDate ? fmt(entry.eventDate) : '—');

  const handleSend = async () => {
    setSendStatus('sending');
    const savedOnFormatted = new Date(entry.savedAt || entry.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

    const payload = {
      projectNumber: idx + 1,
      clientName: entry.clientName || '—',
      exhibition: entry.exhibition || '—',
      location: entry.location || '—',
      eventDate: eventDateFormatted,
      savedOn: savedOnFormatted,
      stall: entry.stall,
      logos: entry.logos || [],
      customization: customization || '—',
    };

    try {
      const res = await fetch('/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        setSendStatus('sent');
        setTimeout(() => setSendStatus(null), 4000);
      } else {
        throw new Error(data.error);
      }
    } catch (err) {
      console.error(err);
      setSendStatus('error');
      setTimeout(() => setSendStatus(null), 4000);
    }
  };

  return (
    <div style={{ background: '#fff', borderRadius: 18, border: '1px solid #ede9e3', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
      <div style={{ background: entry.stall.color, padding: '1rem 2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: entry.stall.accent, marginBottom: 2 }}>Project #{idx + 1}</div>
          <div style={{ fontFamily: 'DM Serif Display', fontSize: 22, color: '#1a1a1a' }}>{entry.clientName || '—'}</div>
        </div>
        <span style={{ fontSize: 11, background: '#fff', color: entry.stall.accent, borderRadius: 20, padding: '4px 14px', fontWeight: 600, border: `1px solid ${entry.stall.accent}40` }}>
          {entry.stall.id}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', borderBottom: '1px solid #f0ede8' }}>
        <div style={{ padding: '1.75rem 2rem', borderRight: '1px solid #f0ede8' }}>
          <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: '#aaa', marginBottom: 16 }}>Project Information</div>
          {[
            ['Client Name', entry.clientName],
            ['Exhibition', entry.exhibition],
            ['Location', entry.location],
            ['Exhibition Date', eventDateFormatted],
            ['Saved On', new Date(entry.savedAt || entry.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })],
          ].map(([label, value]) => (
            <div key={label} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: '#aaa', marginBottom: 2 }}>{label}</div>
              <div style={{ fontSize: 14, fontWeight: 500, color: value ? '#1a1a1a' : '#ccc' }}>{value || '—'}</div>
            </div>
          ))}
        </div>

        <div style={{ borderRight: '1px solid #f0ede8', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: '#aaa', padding: '1.75rem 1.5rem 0.75rem' }}>Stall Preview</div>
          <div style={{ flex: 1, position: 'relative', background: entry.stall.color, minHeight: 180 }}>
            <canvas ref={canvasRef} width={400} height={240} style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }} />
          </div>
          {entry.logos?.length > 0 && (
            <div style={{ padding: '0.75rem 1.5rem', borderTop: '1px solid #f0ede8' }}>
              <div style={{ fontSize: 11, color: '#aaa', marginBottom: 8 }}>Uploaded Logos</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {entry.logos.map((l, i) => (
                  <img key={i} src={l.src} alt={`Logo ${i + 1}`} style={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 6, border: '1px solid #e8e4e0', background: '#faf9f7', padding: 4 }} />
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: '1.75rem 1.5rem', background: '#faf9f7' }}>
          <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: '#aaa', marginBottom: 12 }}>Selected Design</div>
          <div style={{ fontFamily: 'DM Serif Display', fontSize: 18, color: '#1a1a1a', marginBottom: 12 }}>{entry.stall.name}</div>
          {[
            ['Type', entry.stall.type],
            ['Area', entry.stall.area + 'm²'],
            ['Size', entry.stall.size],
            ['Category', entry.stall.category],
          ].map(([label, value]) => (
            <div key={label} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: '#aaa', marginBottom: 1 }}>{label}</div>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#1a1a1a', textTransform: 'capitalize' }}>{value}</div>
            </div>
          ))}
          <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {entry.stall.tags.map((t) => (
              <span key={t} style={{ fontSize: 10, background: entry.stall.color, color: entry.stall.accent, borderRadius: 6, padding: '2px 8px', fontWeight: 500 }}>{t}</span>
            ))}
          </div>
        </div>
      </div>

      <div style={{ padding: '1.5rem 2rem', display: 'flex', gap: 16, alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: '#aaa', marginBottom: 8 }}>Customization Notes</div>
          <textarea
            value={customization}
            onChange={(e) => setCustomization(e.target.value)}
            placeholder="Add any customization requirements, special requests, branding notes, dimensions, materials, etc."
            rows={3}
            style={{ width: '100%', border: '1px solid #e5e1db', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#1a1a1a', background: '#faf9f7', outline: 'none', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6, boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flexShrink: 0 }}>
          <button
            onClick={onCreateProposal}
            style={{
              background: '#5B4FCF',
              border: 'none',
              borderRadius: 10,
              padding: '11px 24px',
              color: '#fff',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              letterSpacing: 0.3,
              whiteSpace: 'nowrap',
            }}
          >
            📄 Create Proposal
          </button>
          <button
            onClick={handleSend}
            disabled={sendStatus === 'sending' || sendStatus === 'sent'}
            style={{
              background: sendStatus === 'sent' ? '#16a34a' : sendStatus === 'error' ? '#dc2626' : '#1a1a1a',
              border: 'none',
              borderRadius: 10,
              padding: '11px 24px',
              color: '#fff',
              fontSize: 13,
              fontWeight: 500,
              cursor: sendStatus === 'sending' || sendStatus === 'sent' ? 'not-allowed' : 'pointer',
              letterSpacing: 0.3,
              whiteSpace: 'nowrap',
            }}
          >
            {sendStatus === 'sending' ? 'Sending…' : sendStatus === 'sent' ? '✓ Sent!' : sendStatus === 'error' ? 'Failed — Retry' : 'Send to Team →'}
          </button>
          <div style={{ fontSize: 11, color: '#bbb', textAlign: 'center' }}>
            Sends to sumit@chalkwalkconsulting.com
          </div>
          <button
            onClick={handleDelete}
            disabled={deleting}
            style={{
              background: 'none',
              border: '1px solid #f0ede8',
              borderRadius: 10,
              padding: '11px 24px',
              color: '#ccc',
              fontSize: 13,
              cursor: deleting ? 'not-allowed' : 'pointer',
              letterSpacing: 0.3,
              whiteSpace: 'nowrap',
              marginTop: 4,
            }}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ClientsPage({ clients, onBack, onDeleteClient }) {
  const [viewProposal, setViewProposal] = useState(null);

  if (viewProposal !== null) {
    return <ProposalPage entry={clients[viewProposal]} projectNumber={viewProposal + 1} onBack={() => setViewProposal(null)} />;
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F7F5F2' }}>
      <header style={{ background: '#fff', borderBottom: '1px solid #ede9e3', padding: '0 2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 116, position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img src={logo} alt="ChalkWalk Design Studio" style={{ width: 220, height: 'auto', objectFit: 'contain' }} />
        </div>
        <button onClick={onBack} style={{ background: '#f5f3f0', border: '1px solid #e8e4e0', borderRadius: 8, padding: '6px 16px', fontSize: 13, color: '#666', cursor: 'pointer' }}>
          ← Back to Planner
        </button>
      </header>

      <main style={{ maxWidth: 1200, margin: '0 auto', padding: '2.5rem 2rem' }}>
        <div style={{ marginBottom: '2.5rem' }}>
          <h1 style={{ fontFamily: 'DM Serif Display', fontSize: 36, color: '#1a1a1a', marginBottom: 6 }}>Clients</h1>
          <p style={{ color: '#888', fontSize: 14 }}>{clients.length} project{clients.length !== 1 ? 's' : ''} saved</p>
        </div>

        {clients.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '5rem 2rem', background: '#fff', borderRadius: 18, border: '1px solid #ede9e3' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>◎</div>
            <h3 style={{ fontFamily: 'DM Serif Display', fontSize: 22, color: '#1a1a1a', marginBottom: 8 }}>No clients yet</h3>
            <p style={{ color: '#aaa', fontSize: 14 }}>Fill in project details and select a stall design to save a client entry.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {clients.map((entry, idx) => (
              <ClientCard key={entry.id ?? idx} entry={entry} idx={idx} onCreateProposal={() => setViewProposal(idx)} onDelete={onDeleteClient} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default ClientsPage;
